import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Bar = styled.span<{ $width: string; $height?: string; $radius?: string }>`
  display: block;
  flex-shrink: 0;
  width: ${props => props.$width};
  height: ${props => props.$height || '10px'};
  border-radius: ${props => props.$radius || '4px'};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 25%,
    rgba(255, 255, 255, 0.14) 50%,
    rgba(255, 255, 255, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Row = styled.div<{ $height: number }>`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  height: ${props => props.$height}px;
  padding: 0 20px;
  overflow: hidden;
`;

const TextLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

type SongRowSkeletonProps = {
  height?: number;
  variant?: 'desktop' | 'mobile';
};

const SongRowSkeleton: React.FC<SongRowSkeletonProps> = ({ height, variant = 'desktop' }) => {
  const isMobile = variant === 'mobile';
  return (
    <Row $height={height || (isMobile ? 64 : 72)} aria-hidden="true">
      {!isMobile && <Bar $width="22px" />}
      <Bar $width="40px" $height="40px" $radius="6px" />
      <TextLines>
        <Bar $width="55%" $height="12px" />
        <Bar $width="35%" />
      </TextLines>
      {!isMobile && (
        <>
          <Bar $width="40px" />
          <Bar $width="140px" />
          <Bar $width="90px" />
        </>
      )}
    </Row>
  );
};

type SongRowSkeletonListProps = {
  count?: number;
  variant?: 'desktop' | 'mobile';
};

export const SongRowSkeletonList: React.FC<SongRowSkeletonListProps> = ({
  count = 3,
  variant = 'desktop',
}) => (
  <div role="status" aria-label="Loading more songs">
    {Array.from({ length: count }, (_, index) => (
      <SongRowSkeleton key={index} variant={variant} />
    ))}
  </div>
);

export default SongRowSkeleton;
